import { Checkbox as ArkCheckBox } from "@ark-ui/solid";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { Check } from "lucide-solid";
import {
	batch,
	type Component,
	createEffect,
	createMemo,
	createSignal,
	For,
	onCleanup,
	Show
} from "solid-js";
import CreditsIcon from "~/Icons/16_Icon_Credits.svg?component-solid";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import * as TeamMethods from "~/solidJs/entities/Teams/methods";
import { Button, formatNumberWithSpaces } from "~/solidJs/shared";
import {
	InvitationStatus,
	type PreviewCreditsDistributionResponse,
	type PreviewDistributionItem,
	TeamApi
} from "~/solidJs/shared/api/TeamApi";
import * as CheckBox from "~/solidJs/shared/ui/atoms/CheckBox";
import * as RadioGroup from "~/solidJs/shared/ui/atoms/RadioGroup";
import { Skeleton } from "~/solidJs/shared/ui/atoms/Skeleton/Skeleton";
import { showToastMessage, ToastMessageTypes } from "~/solidJs/widgets/WorkspaceToast";

interface CreditManagementWidgetProps {
	onSuccess?: () => void;
}

const getInitials = (name: string | null): string => {
	return name?.[0]?.toUpperCase() || "E";
};

const getMemberDisplayName = (member: {
	user_name: string | null;
	company_account_id: number;
}): string => {
	return member.user_name || "Empty Seat";
};

export const CreditManagementWidget: Component<CreditManagementWidgetProps> = (props) => {
	const queryClient = useQueryClient();
	const teamData = createQuery(() => TeamApi.getTeamInfoOptions());
	const transferPoolAvailability = createQuery(() =>
		TeamApi.getTransferPoolAvailabilityOptions()
	);
	const accountsCredits = createQuery(() => TeamApi.getAccountsCreditsOptions());

	const [selectedFromMember, setSelectedFromMember] = createSignal<number | null>(null);
	const [selectedToMembers, setSelectedToMembers] = createSignal<Set<number>>(new Set());
	const [creditAmounts, setCreditAmounts] = createSignal<Map<number, number>>(new Map());
	const [debouncedCreditAmounts, setDebouncedCreditAmounts] = createSignal<Map<number, number>>(
		new Map()
	);
	const [isSubmitting, setIsSubmitting] = createSignal(false);
	const [error, setError] = createSignal<string | null>(null);
	const [previewLoadedSent, setPreviewLoadedSent] = createSignal(false);

	// Scroll state for From list
	const [fromScrollTop, setFromScrollTop] = createSignal(false);
	const [fromScrollBottom, setFromScrollBottom] = createSignal(false);

	// Scroll state for To list
	const [toScrollTop, setToScrollTop] = createSignal(false);
	const [toScrollBottom, setToScrollBottom] = createSignal(false);

	let fromScrollContainer: HTMLDivElement | undefined;

	let debounceTimer: NodeJS.Timeout | null = null;

	// Cleanup debounce timer on unmount
	onCleanup(() => {
		if (debounceTimer) {
			clearTimeout(debounceTimer);
		}
	});

	const members = createMemo(() => TeamMethods.getTeamMembers(teamData.data || null));
	// Include accepted members and empty seats (invitation_status === null)
	const acceptedMembers = createMemo(() =>
		members().filter(
			(m) => m.invitation_status === InvitationStatus.Accepted || m.invitation_status === null
		)
	);
	const currentUser = getUserStore();

	const availableCredits = createMemo(() => {
		const fromMemberId = selectedFromMember();
		if (fromMemberId === null) return 0;
		const member = acceptedMembers().find((m) => m.company_account_id === fromMemberId);
		return member?.credits_balance || 0;
	});

	const totalDistributing = createMemo(() => {
		let total = 0;
		creditAmounts().forEach((amount) => {
			total += amount;
		});
		return total;
	});

	const debouncedTotalDistributing = createMemo(() => {
		let total = 0;
		debouncedCreditAmounts().forEach((amount) => {
			total += amount;
		});
		return total;
	});

	const isDistributionValid = createMemo(() => {
		const total = totalDistributing();
		const available = availableCredits();
		return (
			total > 0 &&
			total <= available &&
			selectedToMembers().size > 0 &&
			selectedFromMember() !== null
		);
	});

	// Prepare preview request when conditions are met (using debounced amounts)
	const previewRequest = createMemo(() => {
		const fromMemberId = selectedFromMember();
		if (fromMemberId === null) return null;

		const selected = selectedToMembers();
		const total = debouncedTotalDistributing();
		if (selected.size === 0 || total === 0) return null;

		const distributions = Array.from(selected)
			.map((companyAccountId) => {
				const amount = debouncedCreditAmounts().get(companyAccountId) || 0;
				if (amount <= 0) return null;
				return {
					target_company_account_id: companyAccountId,
					amount
				};
			})
			.filter(Boolean) as PreviewDistributionItem[];

		if (distributions.length === 0) return null;

		return {
			source_company_account_id: fromMemberId,
			distributions
		};
	});

	// Stable queryKey for preview data - serialize to avoid object reference changes
	const previewQueryKey = createMemo(() => {
		const request = previewRequest();
		if (!request) return null;
		// Serialize to stable string format
		return JSON.stringify({
			source: request.source_company_account_id,
			distributions: request.distributions.map((d) => ({
				target: d.target_company_account_id,
				amount: d.amount
			}))
		});
	});

	// Manual fetch with separate signals to prevent reactive loops
	const [previewResult, setPreviewResult] =
		createSignal<PreviewCreditsDistributionResponse | null>(null);
	const [previewLoading, setPreviewLoading] = createSignal(false);
	const [previewError, setPreviewError] = createSignal<any>(null);
	let fetchAbortController: AbortController | null = null;

	// Fetch when previewRequest changes - use untrack to prevent self-subscription
	createEffect(() => {
		const request = previewRequest();
		const key = previewQueryKey();

		// Cancel previous request
		if (fetchAbortController) {
			fetchAbortController.abort();
			fetchAbortController = null;
		}

		if (!request || !key) {
			// Don't reset existing data - keep last preview visible
			return;
		}

		const controller = new AbortController();
		fetchAbortController = controller;
		setPreviewLoading(true);

		TeamApi.previewCreditsDistribution(request)
			.then((result) => {
				if (controller.signal.aborted) return;
				batch(() => {
					setPreviewResult(result);
					setPreviewLoading(false);
					setPreviewError(null);
				});
			})
			.catch((err) => {
				if (controller.signal.aborted) return;
				batch(() => {
					setPreviewLoading(false);
					setPreviewError(err);
				});
			});

		// Cleanup on effect re-run or component unmount
		onCleanup(() => {
			controller.abort();
		});
	});

	// Create a query-like object for compatibility
	const previewData = {
		get data() {
			return previewResult();
		},
		get isLoading() {
			return previewLoading();
		},
		get isFetching() {
			return previewLoading();
		},
		get error() {
			return previewError();
		}
	};

	const autoDistributeEqually = () => {
		const selected = Array.from(selectedToMembers());
		if (selected.length === 0) return;

		const available = availableCredits();
		const perMember = Math.floor(available / selected.length);

		const newAmounts = new Map<number, number>();
		selected.forEach((companyAccountId) => {
			newAmounts.set(companyAccountId, perMember);
		});

		setCreditAmounts(newAmounts);
	};

	const toggleToMemberSelection = (companyAccountId: number) => {
		const currentFromScroll = fromScrollContainer?.scrollTop || 0;

		const newSelected = new Set<number>(selectedToMembers());
		if (newSelected.has(companyAccountId)) {
			newSelected.delete(companyAccountId);

			const newAmounts = new Map(creditAmounts());
			newAmounts.delete(companyAccountId);
			setCreditAmounts(newAmounts);
		} else {
			newSelected.add(companyAccountId);
		}
		setSelectedToMembers(newSelected);

		setTimeout(() => {
			if (fromScrollContainer) {
				fromScrollContainer.scrollTop = currentFromScroll;
			}
		}, 0);
	};

	const selectAllToMembers = () => {
		const currentFromScroll = fromScrollContainer?.scrollTop || 0;

		const fromId = selectedFromMember();
		const allIds = acceptedMembers()
			.filter((m) => m.company_account_id !== fromId)
			.map((m) => m.company_account_id);
		setSelectedToMembers(new Set<number>(allIds));

		setTimeout(() => {
			if (fromScrollContainer) {
				fromScrollContainer.scrollTop = currentFromScroll;
			}
		}, 0);
	};

	const clearToSelection = () => {
		const currentFromScroll = fromScrollContainer?.scrollTop || 0;

		setSelectedToMembers(new Set<number>());
		setCreditAmounts(new Map());

		setTimeout(() => {
			if (fromScrollContainer) {
				fromScrollContainer.scrollTop = currentFromScroll;
			}
		}, 0);
	};

	const handleScroll = (
		e: Event,
		setScrollTop: (value: boolean) => void,
		setScrollBottom: (value: boolean) => void
	) => {
		const target = e.target as HTMLElement;
		const scrollTop = target.scrollTop;
		const scrollHeight = target.scrollHeight;
		const clientHeight = target.clientHeight;

		setScrollTop(scrollTop > 0);
		setScrollBottom(scrollTop + clientHeight < scrollHeight - 1);
	};

	const handleAmountChange = (companyAccountId: number, value: string) => {
		const numValue = parseInt(value, 10) || 0;
		const newAmounts = new Map(creditAmounts());
		newAmounts.set(companyAccountId, numValue);
		setCreditAmounts(newAmounts);

		// Debounce the preview request
		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => {
			setDebouncedCreditAmounts(newAmounts);
		}, 500);
	};

	// Sync debounced amounts on mount and when amounts change via other methods
	createEffect(() => {
		const amounts = creditAmounts();
		// Only sync if not from handleAmountChange (which has its own debounce)
		if (!debounceTimer) {
			setDebouncedCreditAmounts(amounts);
		}
	});

	// Handle preview errors from backend - show only in toast
	createEffect(() => {
		const previewError = previewData.error;
		const isLoading = previewData.isLoading;

		if (isLoading || !previewError) {
			return;
		}

		// Show backend validation errors in toast only
		const err = previewError as any;
		let errorMessage = "Failed to validate credit distribution";

		if (err.response?.data?.detail) {
			errorMessage = err.response.data.detail;
		} else if (err.response?.data?.message) {
			errorMessage = err.response.data.message;
		}

		showToastMessage({
			title: "Validation Error",
			description: errorMessage,
			type: ToastMessageTypes.Error
		});
	});

	// Memoize auto-distribute check to avoid recalculation
	const isAutoDistribute = createMemo(() => {
		const selected = selectedToMembers();
		const amounts = creditAmounts();
		if (selected.size === 0) return false;

		const amountValues: number[] = [];
		selected.forEach((id) => {
			const amount = amounts.get(id) || 0;
			if (amount > 0) amountValues.push(amount);
		});

		return amountValues.length > 1 && amountValues.every((amt) => amt === amountValues[0]);
	});

	// Track preview loaded event when preview cards are shown
	createEffect(() => {
		const selected = selectedToMembers();
		const total = totalDistributing();
		const hasError = error();
		const fromMemberId = selectedFromMember();

		// Preview is shown when: selectedToMembers > 0 && totalDistributing > 0 && !error
		const isPreviewShown = selected.size > 0 && total > 0 && !hasError;

		if (isPreviewShown && !previewLoadedSent() && fromMemberId && currentUser?.id) {
			EventAnalytics.team.creditReassign.previewLoaded({
				team_id: fromMemberId,
				owner_user_id: currentUser!.id,
				recipients_count: selected.size,
				total_amount: total,
				is_auto_distribute: isAutoDistribute()
			});

			setPreviewLoadedSent(true);
		}

		// Reset the flag when preview is hidden
		if (!isPreviewShown && previewLoadedSent()) {
			setPreviewLoadedSent(false);
		}
	});

	const handleDistribute = async () => {
		const fromMemberId = selectedFromMember();
		if (fromMemberId === null || !isDistributionValid()) return;

		const fromMember = acceptedMembers().find((m) => m.company_account_id === fromMemberId);

		if (!fromMember) {
			setError("Unable to find the selected member");
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const transferPromises = Array.from(selectedToMembers()).map(
				async (companyAccountId) => {
					const amount = creditAmounts().get(companyAccountId) || 0;
					if (amount <= 0) return null;

					return TeamApi.transferCredits({
						source_company_account_id: fromMemberId,
						target_company_account_id: companyAccountId,
						amount
					});
				}
			);

			await Promise.all(transferPromises.filter(Boolean));

			if (fromMemberId && currentUser?.id) {
				EventAnalytics.team.creditReassign.completed({
					team_id: fromMemberId,
					owner_user_id: currentUser!.id,
					recipients_count: selectedToMembers().size,
					total_amount: totalDistributing(),
					is_auto_distribute: isAutoDistribute(),
					source_breakdown_type: "peer_to_peer" // Updated since we don't use breakdown anymore
				});
			}

			// Refetch both accountsCredits and teamInfo to update balances
			queryClient.refetchQueries({ queryKey: ["accountsCredits"] });
			queryClient.refetchQueries({ queryKey: ["teamInfo"] });

			Array.from(selectedToMembers()).forEach((companyAccountId) => {
				const amount = creditAmounts().get(companyAccountId) || 0;
				if (amount <= 0) return;

				const member = acceptedMembers().find(
					(m) => m.company_account_id === companyAccountId
				);
				if (member) {
					showToastMessage({
						title: "Success!",
						description: `${formatNumberWithSpaces(amount)} credits have been added to ${getMemberDisplayName(member)} balance 🎉`,
						type: ToastMessageTypes.Success
					});
				}
			});

			batch(() => {
				setSelectedFromMember(null);
				setSelectedToMembers(new Set<number>());
				setCreditAmounts(new Map());
				setDebouncedCreditAmounts(new Map());
				// Reset preview data
				setPreviewResult(null);
				setPreviewError(null);
				setPreviewLoadedSent(false);
			});

			props.onSuccess?.();
		} catch (err: any) {
			console.error("Transfer credits error:", err);

			let errorType: "insufficient_balance" | "validation_error" | "network_error" =
				"network_error";
			let errorMessage = "Failed to distribute credits";

			if (err.response?.status === 422 && err.response?.data?.detail) {
				errorType = "validation_error";
				const details = err.response.data.detail;
				const missingFields = details
					.filter((d: any) => d.type === "missing")
					.map((d: any) => d.loc[d.loc.length - 1])
					.join(", ");

				errorMessage = missingFields
					? `Missing required fields: ${missingFields}`
					: "Invalid request data";

				setError(errorMessage);
				showToastMessage({
					title: "Validation Error",
					description: errorMessage,
					type: ToastMessageTypes.Error
				});

				const fromId = selectedFromMember();
				if (fromId && currentUser?.id) {
					EventAnalytics.team.creditReassign.failed({
						team_id: fromId,
						owner_user_id: currentUser!.id,
						error_type: errorType,
						error_message: errorMessage
					});
				}
				return;
			}

			if (err.response?.data?.error === "insufficient_balance") {
				errorType = "insufficient_balance";
				errorMessage = err.response?.data?.message || "Insufficient balance";
			} else if (
				err.response?.status === 403 &&
				err.response?.data?.error === "transfer_pool_not_available"
			) {
				errorMessage =
					err.response?.data?.message ||
					"Transfer Pool is not available for your plan. Contact support to enable this feature.";
				queryClient.invalidateQueries({ queryKey: ["transferPoolAvailability"] });
			} else {
				errorMessage = err.response?.data?.message || "Failed to distribute credits";
			}

			// Don't set error state for API errors - just show toast
			// setError is only for validation errors (insufficient balance before submit)
			showToastMessage({
				title: "Error",
				description: errorMessage,
				type: ToastMessageTypes.Error
			});

			const fromId = selectedFromMember();
			if (fromId && currentUser?.id) {
				EventAnalytics.team.creditReassign.failed({
					team_id: fromId,
					owner_user_id: currentUser!.id,
					error_type: errorType,
					error_message: errorMessage
				});
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleReset = () => {
		let stage: "selection" | "distribution" | "preview" = "selection";
		if (selectedFromMember() !== null && selectedToMembers().size > 0) {
			if (creditAmounts().size > 0 && totalDistributing() > 0) {
				stage = "preview";
			} else {
				stage = "distribution";
			}
		}

		const fromId = selectedFromMember();
		if (fromId && currentUser?.id) {
			EventAnalytics.team.creditReassign.cancelled({
				team_id: fromId,
				owner_user_id: currentUser!.id,
				stage
			});
		}

		batch(() => {
			setSelectedFromMember(null);
			setSelectedToMembers(new Set<number>());
			setCreditAmounts(new Map());
			setDebouncedCreditAmounts(new Map());
			setError(null);
			// Reset preview data
			setPreviewResult(null);
			setPreviewError(null);
			setPreviewLoadedSent(false);
		});
	};

	const isTransferPoolAvailable = createMemo(
		() => transferPoolAvailability.data?.is_available ?? false
	);

	const hasAnyActions = createMemo(() => {
		return (
			selectedFromMember() !== null ||
			selectedToMembers().size > 0 ||
			creditAmounts().size > 0
		);
	});

	return (
		<div class="flex w-full flex-col items-start gap-8 rounded-[32px] bg-white p-10">
			<div class="flex flex-col gap-1">
				<h2
					class="text-text-primary"
					style={{
						"font-family": "Poppins",
						"font-size": "24px",
						"font-weight": "600",
						"line-height": "28px",
						"letter-spacing": "-0.12px"
					}}
				>
					Credit management
				</h2>
				<p
					class="self-stretch overflow-hidden text-ellipsis"
					style={{
						height: "18px",
						color: "var(--text-secondary-v-2, #555)",
						"font-family": "Poppins",
						"font-size": "13px",
						"font-weight": "500",
						"line-height": "18px",
						"letter-spacing": "-0.065px"
					}}
				>
					Distribute credits from company pool or transfer between team members
				</p>
			</div>

			<Show when={!transferPoolAvailability.isLoading && !isTransferPoolAvailable()}>
				<div class="flex flex-col items-center gap-4 rounded-xl bg-background-secondary p-6">
					<div class="flex flex-col items-center gap-2 text-center">
						<span class="text-[32px] leading-none">🔒</span>
						<h3 class="typography-text-16-semibold text-text-primary">
							Transfer Pool is not available
						</h3>
						<p class="typography-text-14-regular text-text-secondary">
							Transfer Pool is not available for your plan. Contact support to enable
							this feature.
						</p>
					</div>
					<Button
						variant="PrimaryAccent"
						size="medium"
						onClick={() => {
							window.open("mailto:support@phygital.plus", "_blank");
						}}
					>
						Contact Support
					</Button>
				</div>
			</Show>

			<Show when={!transferPoolAvailability.isLoading && isTransferPoolAvailable()}>
				<div class="grid w-full grid-cols-2 gap-8">
					<div class="flex flex-col gap-4">
						<h3 class="typography-text-14-medium text-text-primary">From</h3>
						<Show
							when={!teamData.isLoading && !accountsCredits.isLoading}
							fallback={
								<div class="flex flex-col gap-2">
									<Skeleton class="h-14" />
									<Skeleton class="h-14" />
								</div>
							}
						>
							<RadioGroup.RadioGroup.Root
								value={selectedFromMember()?.toString() || ""}
								onValueChange={(details) => {
									if (details.value) {
										// Save current scroll position
										const currentScrollTop =
											fromScrollContainer?.scrollTop || 0;

										const newFromId = parseInt(details.value, 10);
										setSelectedFromMember(newFromId);
										const newToMembers = new Set(selectedToMembers());
										if (newToMembers.has(newFromId)) {
											newToMembers.delete(newFromId);
											setSelectedToMembers(newToMembers);
											const newAmounts = new Map(creditAmounts());
											newAmounts.delete(newFromId);
											setCreditAmounts(newAmounts);
										}

										// Restore scroll position after render
										setTimeout(() => {
											if (fromScrollContainer) {
												fromScrollContainer.scrollTop = currentScrollTop;
											}
										}, 0);
									}
								}}
							>
								<div
									class="relative"
									style={{ "max-height": "300px", overflow: "clip" }}
								>
									<Show when={fromScrollTop()}>
										<div class="pointer-events-none absolute top-0 right-0 left-0 z-10 h-16 bg-gradient-to-b from-white to-transparent" />
									</Show>
									<div
										class="flex max-h-[280px] flex-col gap-2 overflow-y-auto"
										onScroll={(e) =>
											handleScroll(e, setFromScrollTop, setFromScrollBottom)
										}
										ref={(el) => {
											fromScrollContainer = el;
											// Initialize scroll state on mount
											setTimeout(() => {
												const hasScroll = el.scrollHeight > el.clientHeight;
												setFromScrollBottom(hasScroll);
											}, 0);
										}}
									>
										<For each={acceptedMembers()}>
											{(member) => {
												const isCurrentUser =
													member.user_id === currentUser?.id;
												return (
													<RadioGroup.RadioGroup.Item
														value={member.company_account_id.toString()}
														class="flex h-14 cursor-pointer items-center gap-2 self-stretch rounded-2xl border border-border-secondary bg-background-secondary py-3 pr-4 pl-4 transition-colors hover:border-border-tertiary"
													>
														<div class="flex flex-1 items-center gap-2">
															<RadioGroup.RadioGroup.ItemControl />
															<RadioGroup.RadioGroup.HiddenInput />
															<div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-background-interaction-tertiary">
																<span class="caption-12-semibold text-text-primary uppercase">
																	{getInitials(member.user_name)}
																</span>
															</div>
															<div class="flex flex-1 flex-col">
																<div class="flex items-center gap-1">
																	<span class="caption-13-regular text-text-primary">
																		{getMemberDisplayName(
																			member
																		)}
																	</span>
																	<Show when={isCurrentUser}>
																		<span class="caption-13-medium text-text-secondary">
																			(you)
																		</span>
																	</Show>
																</div>
															</div>
														</div>
														<div class="flex items-center gap-1">
															<CreditsIcon class="size-4 text-text-primary" />
															<span class="caption-13-medium text-text-primary">
																{formatNumberWithSpaces(
																	member.credits_balance
																)}
															</span>
														</div>
													</RadioGroup.RadioGroup.Item>
												);
											}}
										</For>
									</div>
									<Show when={fromScrollBottom()}>
										<div class="pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-16 bg-gradient-to-t from-white to-transparent" />
									</Show>
								</div>
							</RadioGroup.RadioGroup.Root>
						</Show>
					</div>

					<div class="flex flex-col gap-4">
						<div class="flex items-center justify-between">
							<h3 class="typography-text-14-medium text-text-primary">
								To • {selectedToMembers().size} selected
							</h3>
							<button
								type="button"
								class="typography-text-14-medium text-text-primary hover:underline"
								onClick={
									selectedToMembers().size > 0
										? clearToSelection
										: selectAllToMembers
								}
							>
								{selectedToMembers().size > 0 ? "Clear selection" : "Select all"}
							</button>
						</div>

						<div class="relative" style={{ "max-height": "300px", overflow: "clip" }}>
							<Show when={toScrollTop()}>
								<div class="pointer-events-none absolute top-0 right-0 left-0 z-10 h-16 bg-gradient-to-b from-white to-transparent" />
							</Show>
							<div
								class="flex max-h-[280px] flex-col gap-2 overflow-y-auto"
								onScroll={(e) => handleScroll(e, setToScrollTop, setToScrollBottom)}
								ref={(el) => {
									// Initialize scroll state on mount
									setTimeout(() => {
										const hasScroll = el.scrollHeight > el.clientHeight;
										setToScrollBottom(hasScroll);
									}, 0);
								}}
							>
								<For each={acceptedMembers()}>
									{(member) => {
										const isCurrentUser = member.user_id === currentUser?.id;
										const isSelected = () =>
											selectedToMembers().has(member.company_account_id);
										const isDisabled = () =>
											member.company_account_id === selectedFromMember();

										return (
											<div
												class="flex h-14 items-center gap-2 self-stretch rounded-2xl border py-3 pr-4 pl-4 transition-colors"
												classList={{
													"cursor-pointer hover:border-border-tertiary border-border-secondary bg-background-secondary":
														!isDisabled(),
													"cursor-not-allowed border-border-secondary bg-background-secondary":
														isDisabled()
												}}
												onClick={() => {
													if (!isDisabled()) {
														toggleToMemberSelection(
															member.company_account_id
														);
													}
												}}
											>
												<CheckBox.CheckBox.Root
													checked={isSelected()}
													disabled={isDisabled()}
													onCheckedChange={() => {
														if (!isDisabled()) {
															toggleToMemberSelection(
																member.company_account_id
															);
														}
													}}
													class="pointer-events-none contents"
												>
													<div class="flex flex-1 items-center gap-2">
														<CheckBox.CheckBox.Control>
															<CheckBox.CheckBox.Indicator>
																<Check size={16} />
															</CheckBox.CheckBox.Indicator>
														</CheckBox.CheckBox.Control>
														<ArkCheckBox.HiddenInput hidden />
														<div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-background-interaction-tertiary">
															<span
																class="caption-12-semibold uppercase"
																classList={{
																	"text-text-primary":
																		!isDisabled(),
																	"text-text-tertiary":
																		isDisabled()
																}}
															>
																{getInitials(member.user_name)}
															</span>
														</div>
														<div class="flex flex-1 flex-col">
															<div class="flex items-center gap-1">
																<span
																	class="caption-13-regular"
																	classList={{
																		"text-text-primary":
																			!isDisabled(),
																		"text-text-tertiary":
																			isDisabled()
																	}}
																>
																	{getMemberDisplayName(member)}
																</span>
																<Show when={isCurrentUser}>
																	<span class="caption-13-medium text-text-secondary">
																		(you)
																	</span>
																</Show>
															</div>
														</div>
													</div>
													<div class="flex items-center gap-1">
														<CreditsIcon
															class="size-4"
															classList={{
																"text-text-primary": !isDisabled(),
																"text-text-tertiary": isDisabled()
															}}
														/>
														<span
															class="caption-13-medium"
															classList={{
																"text-text-primary": !isDisabled(),
																"text-text-tertiary": isDisabled()
															}}
														>
															{formatNumberWithSpaces(
																member.credits_balance
															)}
														</span>
													</div>
												</CheckBox.CheckBox.Root>
											</div>
										);
									}}
								</For>
							</div>
							<Show when={toScrollBottom()}>
								<div class="pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-16 bg-gradient-to-t from-white to-transparent" />
							</Show>
						</div>
					</div>
				</div>

				<Show when={selectedFromMember() !== null && selectedToMembers().size > 1}>
					<button
						type="button"
						class="flex items-center justify-center gap-2 self-stretch rounded-md px-7 py-3 transition-colors hover:opacity-80"
						style={{
							background: "var(--interaction-grey-light, #F3F3F4)"
						}}
						onClick={autoDistributeEqually}
					>
						<span class="typography-text-14-medium text-interaction-violet">
							Auto-distribute equally
						</span>
					</button>
				</Show>

				<Show when={selectedFromMember() !== null && selectedToMembers().size > 0}>
					<div class="flex w-full flex-col overflow-hidden rounded-2xl border border-border-secondary bg-background-secondary">
						<Show when={selectedFromMember() !== null}>
							{
								(() => {
									const fromMember = acceptedMembers().find(
										(m) => m.company_account_id === selectedFromMember()
									);
									if (!fromMember) return null;

									const totalDistributing = Array.from(
										selectedToMembers()
									).reduce((sum, id) => sum + (creditAmounts().get(id) || 0), 0);
									const newBalance =
										fromMember.credits_balance - totalDistributing;

									return (
										<div class="flex items-center gap-3 py-3 pr-3 pl-4">
											<div class="flex items-center gap-2">
												<div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-background-interaction-tertiary">
													<span class="caption-12-semibold text-text-primary uppercase">
														{getInitials(fromMember.user_name)}
													</span>
												</div>
												<div class="flex items-center gap-1">
													<span class="typography-text-14-regular text-text-primary">
														{getMemberDisplayName(fromMember)}
													</span>
													<Show
														when={
															fromMember.user_id === currentUser?.id
														}
													>
														<span class="typography-text-14-medium text-text-secondary">
															(you)
														</span>
													</Show>
												</div>
											</div>

											<div class="ml-auto flex items-center gap-2">
												<span class="caption-12-medium text-text-secondary">
													{formatNumberWithSpaces(
														fromMember.credits_balance
													)}
												</span>
												<svg
													width="16"
													height="16"
													viewBox="0 0 16 16"
													fill="none"
													class="shrink-0 text-text-tertiary"
												>
													<path
														d="M3 8H13M13 8L9 4M13 8L9 12"
														stroke="currentColor"
														stroke-width="1.5"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
												<span class="caption-12-medium text-text-primary">
													{formatNumberWithSpaces(newBalance)}
												</span>
											</div>
										</div>
									);
								}) as any
							}
						</Show>

						<For each={Array.from(selectedToMembers())}>
							{(companyAccountId) => {
								const member = acceptedMembers().find(
									(m) => m.company_account_id === companyAccountId
								);
								if (!member) return null;

								const amount = () => creditAmounts().get(companyAccountId) || 0;
								const newBalance = () => member.credits_balance + amount();

								return (
									<div class="flex items-center gap-3 py-3 pr-3 pl-10">
										<svg
											width="16"
											height="16"
											viewBox="0 0 16 16"
											fill="none"
											class="shrink-0 text-text-tertiary"
										>
											<path
												d="M3 8H13M13 8L9 4M13 8L9 12"
												stroke="currentColor"
												stroke-width="1.5"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>

										{/* Member info */}
										<div class="flex items-center gap-2">
											<div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-background-interaction-tertiary">
												<span class="caption-12-semibold text-text-primary uppercase">
													{getInitials(member.user_name)}
												</span>
											</div>
											<div class="flex items-center gap-1">
												<span class="caption-13-regular text-text-primary">
													{getMemberDisplayName(member)}
												</span>
												<Show when={member.user_id === currentUser?.id}>
													<span class="caption-13-medium text-text-secondary">
														(you)
													</span>
												</Show>
											</div>
										</div>

										<div class="ml-auto flex h-10 flex-col items-end justify-center gap-1">
											<Show when={amount() > 0}>
												<span
													class="overflow-hidden text-ellipsis"
													style={{
														color: "var(--status-accent-positive, #2CBA53)",
														"font-family": "Poppins",
														"font-size": "13px",
														"font-weight": "500",
														"line-height": "18px",
														"letter-spacing": "-0.065px"
													}}
												>
													+{formatNumberWithSpaces(amount())}
												</span>
											</Show>

											<div class="flex items-center gap-2">
												<span
													class="overflow-hidden text-ellipsis"
													style={{
														color: "var(--text-secondary, #999)",
														"font-family": "Poppins",
														"font-size": "13px",
														"font-weight": "500",
														"line-height": "18px",
														"letter-spacing": "-0.065px"
													}}
												>
													{formatNumberWithSpaces(member.credits_balance)}
												</span>
												<svg
													width="16"
													height="16"
													viewBox="0 0 16 16"
													fill="none"
													class="shrink-0 text-text-tertiary"
												>
													<path
														d="M3 8H13M13 8L9 4M13 8L9 12"
														stroke="currentColor"
														stroke-width="1.5"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
												<span
													class="overflow-hidden text-ellipsis"
													style={{
														color: "var(--text-secondary-v-2, #555)",
														"font-family": "Poppins",
														"font-size": "13px",
														"font-weight": "500",
														"line-height": "18px",
														"letter-spacing": "-0.065px"
													}}
												>
													{formatNumberWithSpaces(newBalance())}
												</span>
											</div>
										</div>

										<div class="flex h-10 w-40 items-center justify-center gap-4 rounded-md border border-border-tertiary bg-background-gray-light">
											<button
												type="button"
												class="flex size-4 shrink-0 items-center justify-center rounded-full bg-interaction-black transition-colors hover:opacity-80 disabled:opacity-50"
												style={{ width: "16px", height: "16px" }}
												onClick={() => {
													const current = amount();
													if (current > 1000) {
														handleAmountChange(
															companyAccountId,
															String(current - 1000)
														);
													}
												}}
												disabled={amount() < 1000}
											>
												<svg
													width="12"
													height="12"
													viewBox="0 0 12 12"
													fill="none"
													class="shrink-0"
												>
													<path
														d="M2 6H10"
														stroke="white"
														stroke-width="1.5"
														stroke-linecap="round"
													/>
												</svg>
											</button>

											<input
												type="number"
												value={amount()}
												onInput={(e) =>
													handleAmountChange(
														companyAccountId,
														e.currentTarget.value
													)
												}
												class="typography-text-14-medium h-8 w-16 rounded-lg border-0 bg-transparent px-2 text-center text-text-primary outline-none"
												min="0"
												step="1000"
											/>

											<button
												type="button"
												class="flex size-4 shrink-0 items-center justify-center rounded-full bg-interaction-black transition-colors hover:opacity-80"
												style={{ width: "16px", height: "16px" }}
												onClick={() => {
													const current = amount();
													handleAmountChange(
														companyAccountId,
														String(current + 1000)
													);
												}}
											>
												<svg
													width="12"
													height="12"
													viewBox="0 0 12 12"
													fill="none"
													class="shrink-0"
												>
													<path
														d="M6 2V10M2 6H10"
														stroke="white"
														stroke-width="1.5"
														stroke-linecap="round"
													/>
												</svg>
											</button>
										</div>
									</div>
								);
							}}
						</For>
					</div>
				</Show>

				<Show when={previewData.data}>
					<div class="flex w-full items-stretch gap-2">
						{/* From Card */}
						<Show when={selectedFromMember() !== null && previewData.data}>
							{
								(() => {
									const fromMember = acceptedMembers().find(
										(m) => m.company_account_id === selectedFromMember()
									);
									if (!fromMember) return null;

									const preview = previewData.data!;
									const newBalance = preview.source_projected_balance;
									const change = -preview.total_to_distribute;

									return (
										<div class="flex flex-1 items-start rounded-2xl border border-border-secondary bg-gray-100 p-5">
											<div class="flex flex-1 items-start gap-2">
												<div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-background-interaction-tertiary">
													<span class="caption-12-semibold text-text-primary uppercase">
														{getInitials(fromMember.user_name)}
													</span>
												</div>
												<div class="flex flex-1 flex-col gap-2">
													<div class="flex items-center gap-1">
														<span
															style={{
																color: "var(--text-primary, #1A1A1A)",
																"font-family": "Poppins",
																"font-size": "14px",
																"font-weight": "500",
																"line-height": "20px"
															}}
														>
															{getMemberDisplayName(fromMember)}
														</span>
														<Show
															when={
																fromMember.user_id ===
																currentUser?.id
															}
														>
															<span
																style={{
																	color: "var(--text-secondary, #999)",
																	"font-family": "Poppins",
																	"font-size": "14px",
																	"font-weight": "500",
																	"line-height": "20px"
																}}
															>
																(you)
															</span>
														</Show>
													</div>
													<div class="flex items-center justify-between gap-2">
														<div class="flex items-center gap-2">
															<span
																class="overflow-hidden text-ellipsis"
																style={{
																	color: "var(--text-secondary, #999)",
																	"font-family": "Poppins",
																	"font-size": "15px",
																	"font-weight": "500",
																	"line-height": "18px",
																	"letter-spacing": "-0.065px"
																}}
															>
																{formatNumberWithSpaces(
																	fromMember.credits_balance
																)}
															</span>
															<svg
																width="16"
																height="16"
																viewBox="0 0 16 16"
																fill="none"
																class="shrink-0 text-text-tertiary"
															>
																<path
																	d="M3 8H13M13 8L9 4M13 8L9 12"
																	stroke="currentColor"
																	stroke-width="1.5"
																	stroke-linecap="round"
																	stroke-linejoin="round"
																/>
															</svg>
															<span
																class="overflow-hidden text-ellipsis"
																style={{
																	color: "var(--text-secondary-v-2, #555)",
																	"font-family": "Poppins",
																	"font-size": "15px",
																	"font-weight": "500",
																	"line-height": "18px",
																	"letter-spacing": "-0.065px"
																}}
															>
																{formatNumberWithSpaces(newBalance)}
															</span>
														</div>
														<span
															class="overflow-hidden text-ellipsis"
															style={{
																color: "var(--status-accent-error, #E02020)",
																"font-family": "Poppins",
																"font-size": "15px",
																"font-weight": "500",
																"line-height": "18px",
																"letter-spacing": "-0.065px"
															}}
														>
															{formatNumberWithSpaces(change)}
														</span>
													</div>
												</div>
											</div>
										</div>
									);
								}) as any
							}
						</Show>

						<svg
							width="16"
							height="16"
							viewBox="0 0 16 16"
							fill="none"
							class="shrink-0 self-center text-text-tertiary"
						>
							<path
								d="M3 8H13M13 8L9 4M13 8L9 12"
								stroke="currentColor"
								stroke-width="1.5"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>

						<div class="flex flex-1 flex-col gap-4 rounded-2xl border border-border-secondary bg-gray-100 p-5">
							<For each={previewData.data?.members || []}>
								{(previewMember) => {
									const member = acceptedMembers().find(
										(m) =>
											m.company_account_id ===
											previewMember.company_account_id
									);
									if (!member) return null;

									const amount = () => previewMember.transfer_amount;
									const newBalance = () => previewMember.projected_balance;
									const isCurrentUser = member.user_id === currentUser?.id;

									return (
										<div class="flex items-start">
											<div class="flex flex-1 items-start gap-2">
												<div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-background-interaction-tertiary">
													<span class="caption-12-semibold text-text-primary uppercase">
														{getInitials(member.user_name)}
													</span>
												</div>
												<div class="flex flex-1 flex-col gap-2">
													<div class="flex items-center gap-1">
														<span
															style={{
																color: "var(--text-primary, #1A1A1A)",
																"font-family": "Poppins",
																"font-size": "14px",
																"font-weight": "500",
																"line-height": "20px"
															}}
														>
															{getMemberDisplayName(member)}
														</span>
														<Show when={isCurrentUser}>
															<span
																style={{
																	color: "var(--text-secondary, #999)",
																	"font-family": "Poppins",
																	"font-size": "14px",
																	"font-weight": "500",
																	"line-height": "20px"
																}}
															>
																(you)
															</span>
														</Show>
													</div>
													<div class="flex items-center justify-between gap-2">
														<div class="flex items-center gap-2">
															<span
																class="overflow-hidden text-ellipsis"
																style={{
																	color: "var(--text-secondary, #999)",
																	"font-family": "Poppins",
																	"font-size": "15px",
																	"font-weight": "500",
																	"line-height": "18px",
																	"letter-spacing": "-0.065px"
																}}
															>
																{formatNumberWithSpaces(
																	member.credits_balance
																)}
															</span>
															<svg
																width="16"
																height="16"
																viewBox="0 0 16 16"
																fill="none"
																class="shrink-0 text-text-tertiary"
															>
																<path
																	d="M3 8H13M13 8L9 4M13 8L9 12"
																	stroke="currentColor"
																	stroke-width="1.5"
																	stroke-linecap="round"
																	stroke-linejoin="round"
																/>
															</svg>
															<span
																class="overflow-hidden text-ellipsis"
																style={{
																	color: "var(--text-secondary-v-2, #555)",
																	"font-family": "Poppins",
																	"font-size": "15px",
																	"font-weight": "500",
																	"line-height": "18px",
																	"letter-spacing": "-0.065px"
																}}
															>
																{formatNumberWithSpaces(
																	newBalance()
																)}
															</span>
														</div>
														<span
															class="overflow-hidden text-ellipsis"
															style={{
																color: "var(--status-accent-positive, #2CBA53)",
																"font-family": "Poppins",
																"font-size": "15px",
																"font-weight": "500",
																"line-height": "18px",
																"letter-spacing": "-0.065px"
															}}
														>
															+{formatNumberWithSpaces(amount())}
														</span>
													</div>
												</div>
											</div>
										</div>
									);
								}}
							</For>
						</div>
					</div>
				</Show>

				<div class="flex w-full justify-end gap-3">
					<Button
						variant="TertiaryOverlay"
						size="large"
						onClick={handleReset}
						disabled={!hasAnyActions()}
					>
						Reset
					</Button>
					<Button
						variant="PrimaryAccent"
						size="large"
						onClick={handleDistribute}
						disabled={!isDistributionValid() || isSubmitting()}
						pending={isSubmitting()}
					>
						<span class="typography-text-14-medium">Distribute credits</span>
					</Button>
				</div>
			</Show>
		</div>
	);
};
